import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  Divider,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import {
  formatHours,
  formatOperationalDays,
  formatFullAddress,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  AnimatedNumber,
  MotionList,
  MotionItem,
} from './themeHelpers';

const MIDNIGHT_BG = '#0B0F1A';
const MIDNIGHT_SURFACE = '#111827';
const MIDNIGHT_CARD = '#161D2E';
const MIDNIGHT_BORDER = 'rgba(255,255,255,0.06)';
const GOLD = '#C9A84C';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function MidnightTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const galleryImages = images.slice(0, 6);

  const defaultDescription = `${library.name} is a distinguished study environment conceived for those who demand excellence. Refined interiors, absolute quiet, and curated light make every session extraordinary.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;

  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };

  // Glow underline using accentColor
  const accentUnderline = {
    '&::after': {
      content: '""', display: 'block', mt: 1.5,
      height: '2px', width: 40,
      background: `linear-gradient(90deg, ${accentColor}, transparent)`,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: MIDNIGHT_BG, color: 'white' }}>

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', height: { xs: '55vh', md: '80vh' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.5) saturate(0.7)' }}
        />
        <Box sx={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(to bottom, rgba(11,15,26,0.2) 0%, rgba(11,15,26,0.85) 100%)`,
        }} />

        {/* Gold hairline top bar */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
        }} />

        {/* Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              aria-label="Previous image"
              sx={{
                position: 'absolute', left: 24, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.06)', border: `1px solid ${MIDNIGHT_BORDER}`,
                color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <IconButton
              onClick={nextImage}
              aria-label="Next image"
              sx={{
                position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.06)', border: `1px solid ${MIDNIGHT_BORDER}`,
                color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <ArrowForward fontSize="small" />
            </IconButton>
          </>
        )}

        {/* Dots */}
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 80, right: 40, display: 'flex', gap: 1 }}>
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                sx={{
                  width: 6, height: 6, borderRadius: '50%',
                  bgcolor: idx === currentImageIndex ? GOLD : 'rgba(255,255,255,0.25)',
                  cursor: 'pointer', transition: 'all 0.3s',
                }}
              />
            ))}
          </Box>
        )}

        {/* Hero content */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 8 } }}>
          <Container maxWidth="lg">
            <Reveal y={20} duration={0.7}>
              {/* Gold rule */}
              <Box sx={{ width: 48, height: 1, bgcolor: GOLD, mb: 2.5 }} />
              <Typography
                variant="h2"
                sx={{
                  fontFamily: '"Georgia", serif',
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '4rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.5px',
                  mb: 1.5,
                }}
              >
                {library.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, opacity: 0.6 }}>
                <LocationOn fontSize="small" />
                <Typography variant="body1">{library.address}</Typography>
              </Box>
              <Button
                variant="outlined"
                size="large"
                onClick={() => onBookNow()}
                sx={{
                  borderColor: GOLD, color: GOLD,
                  px: 4, py: 1.25,
                  letterSpacing: 1.5,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  '&:hover': { bgcolor: `${GOLD}18`, borderColor: GOLD, boxShadow: `0 0 24px ${GOLD}44` },
                }}
              >
                Reserve a Seat
              </Button>
            </Reveal>
          </Container>
        </Box>
      </Box>

      {/* ── About + Stats ── */}
      <Box sx={{ bgcolor: MIDNIGHT_SURFACE, borderTop: `1px solid ${MIDNIGHT_BORDER}`, borderBottom: `1px solid ${MIDNIGHT_BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Reveal>
                <Typography
                  variant="h4"
                  sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: 'white', mb: 3, ...accentUnderline }}
                >
                  About {library.name}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.9 }}>
                  {library.description || defaultDescription}
                </Typography>
              </Reveal>
            </Grid>

            <Grid item xs={12} md={5}>
              <Reveal delay={0.1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {seatDisplay && (
                    <Box>
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 3, fontSize: '0.65rem' }}>
                        Seat Availability
                      </Typography>
                      <Typography variant="h3" fontWeight={700} sx={{ color: GOLD, fontFamily: '"Georgia", serif' }}>
                        <AnimatedNumber value={seatDisplay} />
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>seats</Typography>
                    </Box>
                  )}

                  <Divider sx={{ borderColor: MIDNIGHT_BORDER }} />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <AccessTime sx={{ color: accentColor, fontSize: 18, mt: 0.3 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                        {hoursStr || 'Contact for hours'}
                      </Typography>
                      {daysStr && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{daysStr}</Typography>
                      )}
                    </Box>
                  </Box>

                  {library.locker_count > 0 && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <LockOutlined sx={{ color: accentColor, fontSize: 18, mt: 0.3 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {library.locker_count} secure lockers
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Plans ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Reveal>
          <Typography variant="overline" sx={{ color: GOLD, letterSpacing: 3, fontWeight: 600 }}>
            Membership
          </Typography>
          <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: 'white', mt: 0.5, mb: 5 }}>
            Curated plans
          </Typography>
        </Reveal>

        {plans.length > 0 ? (
          <Grid container spacing={3}>
            {plans.map((plan) => {
              const planName = plan.name || titleCasePlanType(plan.plan_type);
              const price = formatPrice(plan.price_cents);
              const period = planPeriodLabel(plan.plan_type);
              const discPct = plan.first_time_discount;
              const discPrice = discountedFirstPrice(plan.price_cents, discPct);

              return (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: MIDNIGHT_CARD,
                      border: `1px solid ${MIDNIGHT_BORDER}`,
                      borderRadius: 2,
                      height: '100%',
                      position: 'relative',
                      transition: 'transform 0.3s, border-color 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: `${GOLD}55`,
                        boxShadow: `0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px ${GOLD}22`,
                      },
                    }}
                  >
                    {discPct > 0 && (
                      <Box
                        sx={{
                          position: 'absolute', top: 0, left: 0, right: 0,
                          height: '2px',
                          background: `linear-gradient(90deg, ${accentColor}, transparent)`,
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 3 }}>
                      {discPct > 0 && (
                        <Chip
                          label={`${discPct}% OFF first time`}
                          size="small"
                          sx={{
                            mb: 1.5, bgcolor: `${accentColor}22`, color: accentColor,
                            border: `1px solid ${accentColor}44`,
                            fontWeight: 600, fontSize: '0.65rem',
                          }}
                        />
                      )}

                      <Typography
                        variant="overline"
                        sx={{ color: 'rgba(255,255,255,0.4)', letterSpacing: 2, display: 'block', mb: 1.5 }}
                      >
                        {planName}
                      </Typography>

                      {price && (
                        <>
                          {discPct > 0 && discPrice !== null ? (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'line-through' }}>
                                {price}
                              </Typography>
                              <Typography variant="h4" fontWeight={700} sx={{ color: GOLD, fontFamily: '"Georgia", serif' }}>
                                {formatPrice(discPrice)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                                first time, then {price}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="h3" fontWeight={700} sx={{ color: GOLD, fontFamily: '"Georgia", serif', mb: 0.5 }}>
                              {price}
                            </Typography>
                          )}
                          {period && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 2 }}>
                              {period}
                            </Typography>
                          )}
                        </>
                      )}

                      <Divider sx={{ borderColor: MIDNIGHT_BORDER, mb: 2 }} />

                      <Box sx={{ mb: 2 }}>
                        {plan.duration_days && (
                          <PlanFeature icon={<CalendarToday />} color="rgba(255,255,255,0.5)">
                            Valid {plan.duration_days} days
                          </PlanFeature>
                        )}
                        <PlanFeature icon={<Schedule />} color="rgba(255,255,255,0.5)">
                          {hoursLabel(plan.hours_per_day)}
                        </PlanFeature>
                        {plan.recurring && (
                          <PlanFeature icon={<Repeat />} color={GOLD}>
                            Auto-renews
                          </PlanFeature>
                        )}
                      </Box>

                      {plan.description ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.4)', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 2,
                          }}
                        >
                          {plan.description}
                        </Typography>
                      ) : null}

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => onBookNow(plan)}
                        sx={{
                          borderColor: `${GOLD}44`, color: GOLD,
                          '&:hover': { bgcolor: `${GOLD}10`, borderColor: GOLD },
                          fontWeight: 600, letterSpacing: 0.5,
                        }}
                      >
                        Select
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ bgcolor: MIDNIGHT_CARD, border: `1px solid ${MIDNIGHT_BORDER}`, borderRadius: 2, p: 5, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>Contact us for membership details</Typography>
            <Button
              variant="outlined"
              onClick={() => onBookNow()}
              sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}10` } }}
            >
              Get in Touch
            </Button>
          </Box>
        )}
      </Container>

      {/* ── Gallery ── */}
      <Box sx={{ bgcolor: MIDNIGHT_SURFACE, borderTop: `1px solid ${MIDNIGHT_BORDER}`, borderBottom: `1px solid ${MIDNIGHT_BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Reveal>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: 'white', mb: 4, ...accentUnderline }}>
              Gallery
            </Typography>
          </Reveal>
          <Grid container spacing={1.5}>
            {galleryImages.map((img, idx) => (
              <Grid item xs={6} md={4} key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  onClick={() => openLightbox(idx)}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%', height: 200, objectFit: 'cover',
                    borderRadius: 1, cursor: 'pointer',
                    filter: 'brightness(0.75) saturate(0.8)',
                    transition: 'filter 0.3s, transform 0.3s',
                    '&:hover': { filter: 'brightness(0.95) saturate(1)', transform: 'scale(1.02)' },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Amenities ── */}
      {library.amenities?.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Reveal>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: 'white', mb: 5, ...accentUnderline }}>
              Amenities
            </Typography>
          </Reveal>
          <MotionList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16 }}>
            {library.amenities.map((amenity, idx) => (
              <MotionItem key={idx}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
                    bgcolor: MIDNIGHT_CARD,
                    border: `1px solid ${MIDNIGHT_BORDER}`,
                    borderRadius: 1.5,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: `${GOLD}33` },
                  }}
                >
                  <Box sx={{ color: GOLD, display: 'flex' }}>
                    {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 20 } })}
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>{amenity}</Typography>
                </Box>
              </MotionItem>
            ))}
          </MotionList>
        </Container>
      )}

      {/* ── Location ── */}
      <Box sx={{ bgcolor: MIDNIGHT_SURFACE, borderTop: `1px solid ${MIDNIGHT_BORDER}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Reveal>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: 'white', mb: 4, ...accentUnderline }}>
              Location
            </Typography>
          </Reveal>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: 300, borderRadius: 2, overflow: 'hidden', border: `1px solid ${MIDNIGHT_BORDER}` }}>
                {library.lat && library.lng ? (
                  <iframe
                    title="Library Location"
                    width="100%" height="100%"
                    style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg)' }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: MIDNIGHT_CARD }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)' }}>Map unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                {fullAddress && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LocationOn sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>Address</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{fullAddress}</Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Phone sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>Phone</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Email sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>Email</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, textAlign: 'center', position: 'relative' }}>
        {/* Hairline rule at top */}
        <Box sx={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />
        <Container maxWidth="sm">
          <Reveal>
            <Typography variant="h4" sx={{ fontFamily: '"Georgia", serif', fontWeight: 700, color: 'white', mb: 2 }}>
              Begin your best work
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 5, lineHeight: 1.7 }}>
              Reserve your place at {library.name} and experience study elevated.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={() => onBookNow()}
              sx={{
                borderColor: GOLD, color: GOLD,
                px: 6, py: 1.5,
                letterSpacing: 1.5, fontWeight: 600,
                fontSize: '0.85rem', textTransform: 'uppercase',
                '&:hover': { bgcolor: `${GOLD}12`, boxShadow: `0 0 32px ${GOLD}33` },
              }}
            >
              Book Now
            </Button>
          </Reveal>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ borderTop: `1px solid ${MIDNIGHT_BORDER}`, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)', fontFamily: '"Georgia", serif' }}>
              {library.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)' }}>
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: MIDNIGHT_BG } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          aria-label="Close gallery"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'rgba(255,255,255,0.6)', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={galleryImages[lightboxIdx]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default MidnightTheme;
